import type { GameMode } from '@shared/chart';

/**
 * localStorage key for the most recently visited game mode. Used by the
 * mode-picker landing page and the TopNav to default to whatever the
 * player was last playing — so a returning Pulse regular doesn't get
 * stuck staring at the picker every visit.
 *
 * Schema: a single string, one of 'pulse' / 'circles' / 'piano' /
 * 'guitar' / 'waves' / 'jump'. Anything else is treated as no preference.
 *
 * Note: `'jump'` now names the Geometry-Dash-cube mode. The original Pulse
 * mode was briefly called "Jump" pre-launch; that migration rewrote stored
 * values to `'pulse'` on read and has long since drained, so `'jump'` is
 * safe to treat as the current Jump mode.
 */
const MODE_PREF_KEY = 'gd-mode-pref';

const VALID_MODES: ReadonlySet<GameMode> = new Set([
  'pulse',
  'circles',
  'piano',
  'guitar',
  'waves',
  'jump',
]);

function isGameMode(value: unknown): value is GameMode {
  return typeof value === 'string' && VALID_MODES.has(value as GameMode);
}

/**
 * Reads the persisted mode preference. Returns null if the player has
 * never picked a mode (so the picker can render as the canonical
 * landing experience for new visitors). Safe to call from SSR/test
 * environments where `localStorage` isn't available.
 */
export function loadModePref(): GameMode | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(MODE_PREF_KEY);
    return isGameMode(raw) ? raw : null;
  } catch (err) {
    console.warn('Failed to read mode preference from localStorage:', err);
    return null;
  }
}

/**
 * Persists the player's most recent mode. Called on every mode-page
 * navigation so the picker can offer a "continue where you left off"
 * default and the TopNav can know which mode-specific links to render
 * even when the user is on a universal page like /profile.
 */
export function saveModePref(mode: GameMode): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(MODE_PREF_KEY, mode);
  } catch (err) {
    console.warn('Failed to persist mode preference to localStorage:', err);
  }
}
